/**
 * LocationPickerButton Component
 *
 * Button component that displays current location and opens LocationPickerModal
 * Used in contractor profile settings
 */

'use client';

import { useState } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { LocationPickerModal } from '@/src/features/modals/LocationPickerModal';

interface LocationData {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

export interface LocationPickerButtonProps {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  error?: string;
  onLocationChange: (location: LocationData) => void;
  onLocationSave: (location: LocationData) => Promise<void>;
  onClearError?: () => void;
}

export function LocationPickerButton({
  address,
  city,
  province,
  postalCode,
  latitude,
  longitude,
  error,
  onLocationChange,
  onLocationSave,
  onClearError,
}: LocationPickerButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Format location for display - compact version
  const formatLocation = () => {
    // Priority: City, Province or Address
    if (city && province) {
      return `${city}, ${province}`;
    }
    if (city) {
      return city;
    }
    if (address) {
      return address;
    }
    // If no address details but has coordinates
    if (latitude !== null && longitude !== null) {
      return `Location set`;
    }
    return 'Click to set location';
  };

  // Get full address for subtitle
  const getFullAddress = () => {
    const parts: string[] = [];
    if (address) parts.push(address);
    if (postalCode) parts.push(postalCode);
    return parts.join(', ');
  };

  const hasCoordinates = latitude !== null && longitude !== null;
  const hasLocation = hasCoordinates;

  const handleLocationSelected = async (location: LocationData) => {
    // Update form state
    onLocationChange(location);
    if (onClearError) {
      onClearError();
    }
    // Save to server immediately
    await onLocationSave(location);
    // Modal will close automatically on success
  };

  return (
    <div>
      {/* Location Display Button */}
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={`w-full px-5 py-3 border rounded-2xl text-left transition-all group hover:border-accent-primary hover:bg-background-secondary ${
          error ? 'border-accent-error' : 'border-border-primary'
        }`}
      >
        <div className="flex items-center gap-3">
          {/* Location Icon */}
          <Icon
            name="map-pin"
            size="md"
            className={hasLocation ? 'text-accent-primary' : 'text-text-tertiary'}
          />

          <div className="flex-1 min-w-0">
            {/* Main Location Text */}
            <Typography
              variant="body"
              className={`truncate ${hasLocation ? 'text-text-primary' : 'text-text-tertiary'}`}
            >
              {formatLocation()}
            </Typography>

            {/* Full Address (if available) */}
            {hasLocation && getFullAddress() && (
              <Typography variant="bodySm" color="secondary" className="truncate mt-0.5">
                {getFullAddress()}
              </Typography>
            )}
          </div>

          {/* Edit Icon */}
          <Icon
            name="pencil"
            size="sm"
            className="text-text-tertiary group-hover:text-accent-primary transition-colors flex-shrink-0"
          />
        </div>
      </button>

      {/* Error Message */}
      {error && (
        <Typography variant="bodySm" className="text-accent-error mt-1">
          {error}
        </Typography>
      )}

      {/* Hint */}
      <Typography variant="bodySm" color="secondary" className="mt-1">
        Click on map to set your base location. Address details are optional.
      </Typography>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onLocationSelected={handleLocationSelected}
        initialLocation={{
          address,
          city,
          province,
          postalCode,
          latitude: latitude ?? undefined,
          longitude: longitude ?? undefined,
        }}
        title="Set Your Service Location"
      />
    </div>
  );
}
